import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import { authRequestCode, authVerifyCode, authLoginPassword } from '../lib/api';
import SEOHead from '../components/SEOHead';
import { Mail, KeyRound, Loader2, CheckCircle2 } from 'lucide-react';

type Step = 'email' | 'code' | 'success';
type Mode = 'code' | 'password'; // v5 blok 7: beide loginmethodes

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const { user, setUser, authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // V5.30: wie al ingelogd is hoort niet op de loginpagina — meteen door
  // naar de juiste plek (voorkomt het "vast op login"-scherm na test-modus).
  useEffect(() => {
    if (authLoading || !user) return;
    const explicitRedirect = searchParams.get('redirect');
    const roleTarget =
      (user.role === 'owner' || user.role === 'superadmin') ? '/admin'
      : user.role === 'reseller' ? '/reseller-dashboard'
      : '/dashboard';
    navigate(explicitRedirect ?? roleTarget, { replace: true });
  }, [authLoading, user, navigate, searchParams]);

  const [step, setStep] = useState<Step>('email');
  const [mode, setMode] = useState<Mode>('code');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const emailRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === 'code') setTimeout(() => codeRef.current?.focus(), 100);
    else setTimeout(() => emailRef.current?.focus(), 100);
  }, [step]);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setLoading(true);
    try {
      await authRequestCode(email.trim().toLowerCase());
      setStep('code');
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Er is iets misgegaan. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const redirectAfterLogin = (user: { role: string }) => {
    const explicitRedirect = searchParams.get('redirect');
    const roleTarget =
      (user.role === 'owner' || user.role === 'superadmin') ? '/admin'
      : user.role === 'reseller' ? '/reseller-dashboard'
      : '/dashboard';
    setTimeout(() => navigate(explicitRedirect ?? roleTarget), 1200);
  };

  // v5 blok 7: klassieke paswoord-login (afspraak: handelaars gebruiken
  // een paswoord; particulieren mogen de mailcode blijven gebruiken).
  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError('');
    setLoading(true);
    try {
      const { user } = await authLoginPassword(email.trim().toLowerCase(), password);
      setUser(user);
      setStep('success');
      redirectAfterLogin(user);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Ongeldige combinatie van e-mail en paswoord.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError('');
    setLoading(true);
    try {
      const { user } = await authVerifyCode(email.trim().toLowerCase(), code.trim());
      setUser(user);
      setStep('success');
      // Redirect based on role:
      //   owner/superadmin → /admin
      //   reseller         → /reseller-dashboard
      //   customer         → /dashboard
      const explicitRedirect = searchParams.get('redirect');
      const roleTarget =
        (user.role === 'owner' || user.role === 'superadmin') ? '/admin'
        : user.role === 'reseller' ? '/reseller-dashboard'
        : '/dashboard';
      const target = explicitRedirect ?? roleTarget;
      setTimeout(() => navigate(target), 1200);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Ongeldige code. Controleer uw e-mail en probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center bg-[#faf7f3]">
      <SEOHead page="login" />
      <div className="w-full max-w-md mx-4">
        <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
          <div className="text-center mb-8">
            <div className="w-14 h-14 bg-[#c47a3a] rounded-sm mx-auto flex items-center justify-center mb-4">
              <span className="text-white font-bold text-xl">G</span>
            </div>
            <h1 className="text-2xl font-bold">
              {step === 'success' ? 'Welkom!' : t('login.title')}
            </h1>
            <p className="text-sm text-[#8a7e74] mt-1">
              {step === 'email' && 'Voer uw e-mailadres in om in te loggen.'}
              {step === 'code' && `Code verstuurd naar ${email}`}
              {step === 'success' && 'U wordt doorgestuurd…'}
            </p>
          </div>

          {step === 'email' && (
            <form onSubmit={mode === 'code' ? handleRequestCode : handlePasswordLogin} className="space-y-4">
              {/* v5 blok 7: keuze tussen code-per-mail en paswoord */}
              <div className="grid grid-cols-2 gap-1 p-1 bg-[#faf7f3] rounded-lg">
                {([['code', 'Code per e-mail'], ['password', 'Paswoord']] as const).map(([m, label]) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setMode(m); setError(''); }}
                    className={`py-2 rounded-md text-sm font-semibold transition-colors ${mode === m ? 'bg-white shadow text-[#c47a3a]' : 'text-[#8a7e74] hover:text-[#4a4a4a]'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4a4a4a] mb-1">E-mailadres</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                  <input
                    ref={emailRef}
                    type="email"
                    required
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="naam@bedrijf.be"
                    className="w-full pl-10 pr-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]"
                  />
                </div>
              </div>
              {mode === 'password' && (
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">Paswoord</label>
                  <div className="relative">
                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-sm focus:outline-none focus:border-[#c47a3a]"
                    />
                  </div>
                  <p className="text-xs text-[#8a7e74] mt-1">
                    Paswoord vergeten? Log in via "Code per e-mail" en stel in uw account een nieuw paswoord in.
                  </p>
                </div>
              )}
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button type="submit" disabled={loading || !email.trim() || (mode === 'password' && !password)} className="btn-primary w-full !py-3 flex items-center justify-center gap-2 disabled:opacity-50">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? 'Bezig…' : mode === 'code' ? 'Stuur code' : 'Inloggen'}
              </button>
              <p className="text-center text-xs text-[#8a7e74]">
                Nog geen account?{' '}
                <a href="/register" className="text-[#c47a3a] font-medium hover:underline">Registreer hier</a>
              </p>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#4a4a4a] mb-1">6-cijferige code</label>
                <div className="relative">
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                  <input
                    ref={codeRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    value={code}
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                    placeholder="123456"
                    className="w-full pl-10 pr-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-sm text-center tracking-widest font-mono focus:outline-none focus:border-[#c47a3a]"
                  />
                </div>
                <p className="text-xs text-[#8a7e74] mt-1">Controleer uw inbox (en spammap).</p>
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <button type="submit" disabled={loading || code.length < 6} className="btn-primary w-full !py-3 flex items-center justify-center gap-2 disabled:opacity-50">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? 'Verifiëren…' : 'Inloggen'}
              </button>
              <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }}
                className="w-full py-2 text-sm text-[#8a7e74] hover:text-[#4a4a4a] transition-colors">
                Ander e-mailadres gebruiken
              </button>
            </form>
          )}

          {step === 'success' && (
            <div className="flex flex-col items-center py-4 gap-3">
              <CheckCircle2 size={48} className="text-green-500" />
              <p className="text-base font-semibold text-[#1a1a1a]">Succesvol ingelogd!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
